import React, { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./style.css";
import { getSubtasksByProjectName, getAllBugsByProject } from "../../services/homeService";

const SubtaskBugsTable = ({ userBugsData, allProjectBugs, loading, approvers = [] }) => {
  const [subtasks, setSubtasks] = useState([]);
  const [subtaskBugs, setSubtaskBugs] = useState({});
  const [allBugs, setAllBugs] = useState([]);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  const { selectedProject } = useSelector((state) => state.bugDetails);
  const { dashboardDataByprojId } = useSelector((state) => state.dashboardDetails);

  // Fetch subtasks for the current project and all bugs
  useEffect(() => {
    const fetchData = async () => {
      const projectName = selectedProject?.name || dashboardDataByprojId?.projectName;
      const projectId = selectedProject?.id || dashboardDataByprojId?.projectId;
      
      if (!projectName || !projectId) return;

      try {
        setLoadingSubtasks(true);
        
        // Fetch all subtasks
        const subtasksData = await getSubtasksByProjectName(projectName);
        setSubtasks(subtasksData || []);
        
        // Fetch ALL bugs including deleted ones
        const allProjectBugs = await getAllBugsByProject(projectId);
        setAllBugs(allProjectBugs || []);
        
        // Organize bugs by subtask
        const subtaskBugsMap = {};
        if (Array.isArray(allProjectBugs)) {
          allProjectBugs.forEach(bug => {
            if (bug.subtaskId) {
              if (!subtaskBugsMap[bug.subtaskId]) {
                subtaskBugsMap[bug.subtaskId] = [];
              }
              subtaskBugsMap[bug.subtaskId].push(bug);
            }
          });
        }
        
        setSubtaskBugs(subtaskBugsMap);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingSubtasks(false);
      }
    };
    
    fetchData();
  }, [selectedProject?.name, selectedProject?.id, dashboardDataByprojId?.projectName, dashboardDataByprojId?.projectId]);

  // Calculate bugs by subtask with missed bugs identification
  const subtaskData = useMemo(() => {
    if (!subtasks || subtasks.length === 0) {
      return [];
    }

    try {
      const processedSubtasks = [];
      
      subtasks.forEach(subtask => {
        const subtaskName = subtask.summary || subtask.name || 'Uncategorized';
        const subtaskId = subtask.id;
        const bugs = subtaskBugs[subtaskId] || [];
        
        let validBugsCount = 0;
        let invalidBugsCount = 0;
        let missedBugsCount = 0;
        let totalBugsCount = bugs.length;
        
        const assigneeName = subtask.assigneeDisplayName || 'Unassigned';
        let reviewerName = subtask.reviewerDisplayName || 'N/A';
        
        // Track reviewers who raised bugs (found missed bugs)
        const reviewersWhoRaisedBugs = new Set();
        
        bugs.forEach(bug => {
          const isDeleted = bug.isDeleted === true;
          const createdBy = bug.createdBy || '';
          const isMissedBug = approvers.includes(createdBy);
          
          if (isMissedBug) {
            missedBugsCount++;
            // Add the reviewer who raised this bug
            const reviewerDisplayName = bug.createdByDisplayName || bug.createdBy || '';
            if (reviewerDisplayName) {
              reviewersWhoRaisedBugs.add(reviewerDisplayName);
            }
          } else if (isDeleted) {
            invalidBugsCount++;
          } else {
            validBugsCount++;
          }
        });
        
        // If any bugs were raised by reviewers, show their names
        if (reviewersWhoRaisedBugs.size > 0) {
          reviewerName = Array.from(reviewersWhoRaisedBugs).join(', ');
        }
        
        // Calculate quality percentage using the formula: (1 - (missedBugs / (validBugs + missedBugs))) * 100
        // Where missedBugs = E2 and validBugs = C2
        // If no bugs at all (validBugs + missedBugs = 0), quality is 100%
        const totalBugsForQuality = validBugsCount + missedBugsCount;
        const qualityPercent = totalBugsForQuality > 0 
          ? ((1 - (missedBugsCount / totalBugsForQuality)) * 100).toFixed(2)
          : 100;
        
        const subtaskDataObj = {
          taskId: subtaskName,
          assignedTo: assigneeName,
          reviewerName: reviewerName,
          bugCountByAssignee: validBugsCount,
          missedBugCount: missedBugsCount,
          invalidBugCount: invalidBugsCount,
          qualityPercent: parseFloat(qualityPercent)
        };
        
        processedSubtasks.push(subtaskDataObj);
      });
      
      return processedSubtasks;
    } catch (error) {
      console.error("Error processing subtask data:", error);
      return [];
    }
  }, [subtasks, subtaskBugs, approvers]);

  // Helper function to get quality color
  const getQualityColor = (quality) => {
    if (quality >= 95) return '#90EE90'; // Light green
    if (quality >= 90) return '#FFFFE0'; // Light yellow
    return '#FFB6C1'; // Light red
  };

  if (loading || loadingSubtasks) {
    return (
      <div className="dashboard-graph">
        <div className="chart-container" style={{ width: "100%", minHeight: 400 }}>
          <h2>Bugs Raised & Missed Per Subtask</h2>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <p>Loading subtask data and bug statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subtaskData || subtaskData.length === 0) {
    return (
      <div className="dashboard-graph">
        <div className="chart-container" style={{ width: "100%", minHeight: 400 }}>
          <h2>Bugs Raised & Missed Per Subtask</h2>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <p>No subtask data available for this project</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-graph">
      <div className="chart-container" style={{ width: "100%", minHeight: 600 }}>
        <h2>Bugs Raised & Missed Per Subtask</h2>
        
        <div style={{ overflowX: 'auto' }}>
          <table className="subtask-bugs-table" style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '14px',
            tableLayout: 'fixed'
          }}>
            <colgroup>
              <col style={{ width: '20%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5', height: '50px' }}>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '16px 8px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#000'
                }}>Task ID</th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '16px 8px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#000'
                }}>Assigned To</th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '16px 8px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#000'
                }}>Reviewer Name</th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '16px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#000'
                }}>Bug Count by Assignee</th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '16px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#000'
                }}>Missed Bug Count (Found by Reviewer)</th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '16px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#000'
                }}>Invalid Bug Count</th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '16px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#000'
                }}>Quality %</th>
              </tr>
            </thead>
            <tbody>
              {subtaskData.map((row, index) => (
                <tr key={index} style={{ 
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                  height: '50px'
                }}>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '14px 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{row.taskId}</td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '14px 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{row.assignedTo}</td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '14px 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{row.reviewerName}</td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '14px 8px',
                    textAlign: 'center' 
                  }}>{row.bugCountByAssignee}</td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '14px 8px',
                    textAlign: 'center' 
                  }}>{row.missedBugCount}</td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '14px 8px',
                    textAlign: 'center' 
                  }}>{row.invalidBugCount}</td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '14px 8px',
                    textAlign: 'center',
                    backgroundColor: getQualityColor(row.qualityPercent),
                    fontWeight: '600'
                  }}>{row.qualityPercent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubtaskBugsTable;
